import { motion } from "framer-motion";
import type { ReactNode } from "react";
import { useWindowContext } from "./WindowContext";
import { testIds } from "../../lib/testIds";
import { getXpIcon } from "../../lib/desktop/iconMap";

interface DesktopIconProps {
  label: string;
  /**
   * Either a ReactNode (legacy emoji span — `<span>📁</span>`) or omitted
   * to use the XP PNG resolved from `getXpIcon(path)`.
   */
  icon?: ReactNode;
  path: string;
  selected: boolean;
  onSelect: () => void;
  onContextMenu: (e: React.MouseEvent, path: string) => void;
  scale?: number;
}

export function DesktopIcon({ label, icon, path, selected, onSelect, onContextMenu, scale = 1 }: DesktopIconProps) {
  const mobile = scale < 1;
  const { getWindow, openWindow, focusWindow } = useWindowContext();
  const xpIcon = icon === undefined ? getXpIcon(path) : null;

  function activate() {
    const win = getWindow(path);
    if (!win) {
      openWindow(path);
    } else if (win.state === "closed" || win.state === "minimized") {
      openWindow(path);
    } else {
      focusWindow(path);
    }
  }

  function handleClick(e: React.MouseEvent) {
    e.stopPropagation();
    if (selected) {
      activate();
    } else {
      onSelect();
    }
  }

  function handleDoubleClick(e: React.MouseEvent) {
    e.stopPropagation();
    activate();
  }

  const tileClasses = [
    "flex flex-col items-center cursor-pointer select-none",
    mobile ? "gap-0.5 p-1.5 min-w-[4rem]" : "gap-1 p-2 min-w-[72px]",
  ].join(" ");

  const iconBoxClasses = [
    "flex items-center justify-center rounded-sm",
    mobile ? "w-16 h-16" : "w-[64px] h-[64px]",
    selected ? "bg-white/20" : "bg-transparent",
  ].join(" ");

  // XP-style label: white text + readable text-shadow on the royal-blue
  // wallpaper. Selected = solid blue rectangle (matches the desktop
  // selection rectangle in real XP).
  const labelClasses = [
    "text-center px-1 leading-tight max-w-[105px] break-words",
    mobile ? "text-[0.875rem]" : "text-[12px]",
    selected
      ? "bg-[var(--xp-selection)] text-white"
      : "text-white",
  ].join(" ");

  return (
    <div
      className="relative"
      onContextMenu={(e) => onContextMenu(e, path)}
      data-testid={testIds.desktop.icon(path)}
      title={`Open ${label}`}
    >
      <motion.div
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        className={tileClasses}
      >
        <div className={iconBoxClasses}>
          {xpIcon !== null ? (
            <img
              src={xpIcon}
              alt=""
              draggable={false}
              className={mobile ? "w-12 h-12" : "w-12 h-12"}
              // SVGs render crisp at any size; PNGs get the pixelated
              // hint so the upscaled 22-48px XP icons stay sharp.
              style={{
                imageRendering: xpIcon.endsWith(".svg") ? "auto" : "pixelated",
              }}
            />
          ) : (
            // Legacy emoji icon — kept for callers that still pass a
            // ReactNode (the existing FirstVisitOverlay tour relies on it).
            <span style={{ fontSize: mobile ? "2.2rem" : 44, lineHeight: 1 }}>
              {icon}
            </span>
          )}
        </div>
        <span
          className={labelClasses}
          style={{
            // Drop-shadow so white labels stay readable on bright
            // wallpaper patches without the cream chip the Win95 build
            // used. Honors memory `feedback_no_light_grey_text` because
            // selected state uses solid blue / white.
            textShadow: selected
              ? "none"
              : "0 1px 2px rgba(0,0,0,0.85), 0 0 2px rgba(0,0,0,0.6)",
          }}
        >
          {label}
        </span>
      </motion.div>
    </div>
  );
}
